import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaDiscord } from 'react-icons/fa';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { config, validateConfig } from './config';

const API_BASE_URL = config.api.baseUrl;

// Custom hook for fetching Discord role and channel information
function useDiscordInfo(roleId, channelId) {
  const [discordInfo, setDiscordInfo] = useState({ role: null, channel: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDiscordInfo = async () => {
      if (!roleId && !channelId) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (roleId) params.append('role_id', roleId);
        if (channelId) params.append('channel_id', channelId);

        const response = await axios.get(`${API_BASE_URL}/api/discord-info?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setDiscordInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch Discord info:', error);
        // Set fallback values on error
        setDiscordInfo({ 
          role: roleId ? { id: roleId, name: roleId } : null,
          channel: channelId ? { id: channelId, name: channelId } : null
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDiscordInfo();
  }, [roleId, channelId]);

  return { discordInfo, loading };
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authAttempts, setAuthAttempts] = useState(new Set()); // Track auth attempts to prevent loops
  const [lastAuthError, setLastAuthError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          setUser(decoded);
          setAuthAttempts(new Set()); // Clear auth attempts if user is already authenticated
        } else {
          localStorage.removeItem('token');
          console.log('Token expired, removing from localStorage');
          setUser(null);
        }
      } else {
        console.log('No valid token found');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // 無効なトークンの場合はローカルストレージから削除
      localStorage.removeItem('token');
      setUser(null);
    }
    setLoading(false);
  };

  const handleDiscordCallback = useCallback(async (code) => {
    // Prevent infinite loops - check if we've already tried this code
    if (authAttempts.has(code)) {
      console.warn('[Auth] Preventing duplicate authentication attempt with code:', code.substring(0, 10) + '...');
      setError('認証処理が既に実行されています。ページをリロードして再試行してください。');
      setLoading(false);
      return;
    }

    // Add code to attempted set
    setAuthAttempts(prev => new Set([...prev, code]));

    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      console.log('[Auth] Starting Discord callback with code:', code.substring(0, 10) + '...');
      console.log('[Auth] API Base URL:', API_BASE_URL);
      console.log('[Auth] Redirect URI:', `${window.location.origin}/auth/callback`);

  // redirect_uri はDiscordのトークン交換で必須だが、バックエンド側で受け取ってそのままDiscordへ送る。
  // フロントエンドのコールバックエンドポイントを指定する
      const response = await axios.post(`${API_BASE_URL}/api/auth/discord/callback`, {
        code,
        redirect_uri: `${window.location.origin}/auth/callback`
      });

      console.log('[Auth] Backend response received:', {
        status: response.status,
        hasToken: !!response.data?.token,
        hasUser: !!response.data?.user
      });

      const { token, user } = response.data;

      // レスポンスにトークンが含まれているかチェック
      if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Invalid token received from server');
      }

      // JWTトークンの形式チェック
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid JWT token format received from server');
      }

      localStorage.setItem('token', token);
      setUser(user);
      setLastAuthError(null); // Clear auth error on success
      setAuthAttempts(new Set()); // Clear auth attempts on success

      console.log('[Auth] Authentication successful for user:', user?.username);

      // Restore cast_id if it was saved before OAuth redirect
      const returnCastId = localStorage.getItem('return_cast_id');
      if (returnCastId) {
        console.log('[Auth] Restoring cast_id path:', returnCastId);
        localStorage.removeItem('return_cast_id');
        navigate(`/${returnCastId}`, { replace: true });
      } else {
        // Clear URL parameters（codeの使い回し防止）
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('[Auth] Discord callback error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: code.substring(0, 10) + '...'
      });

      setLastAuthError({
        timestamp: new Date().toISOString(),
        code: code.substring(0, 10) + '...',
        message: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      });

      // エラー時はトークンをクリアして確実にログイン状態を解除
      localStorage.removeItem('token');
      setUser(null);
      setAuthAttempts(new Set()); // 認証試行履歴もクリア
      
      // User-friendly error messages
      let errorMessage = '認証処理中にエラーが発生しました。';
      
      if (error.response?.status === 500) {
        errorMessage = 'サーバーエラーが発生しました。しばらく時間を置いてから再度ログインしてください。';
      } else if (error.response?.status === 400) {
        errorMessage = '認証コードが無効です。再度ログインをお試しください。';
      } else if (error.response?.status === 401) {
        errorMessage = '認証に失敗しました。Discord アプリケーションの設定を確認するか、再度ログインをお試しください。';
      } else if (error.message.includes('Invalid token') || error.message.includes('Invalid JWT')) {
        errorMessage = 'サーバーから無効な認証情報が返されました。再度ログインをお試しください。';
      } else {
        errorMessage = '認証処理中にエラーが発生しました。ページをリロードして再度ログインをお試しください。';
      }
      
      setError(errorMessage);
      
      // Restore cast_id if it was saved, but clear it after
      const returnCastId = localStorage.getItem('return_cast_id');
      localStorage.removeItem('return_cast_id'); // 必ずクリア
      
      if (returnCastId) {
        console.log('[Auth] Error occurred, returning to cast_id path for retry:', returnCastId);
        navigate(`/${returnCastId}`, { replace: true });
      } else {
        console.log('[Auth] Error occurred, returning to home (login page)');
        navigate('/', { replace: true });
      }
    }
    setLoading(false);
  }, [navigate, authAttempts]); // eslint-disable-next-line

  useEffect(() => {
    validateConfig();
    checkAuthStatus();
  }, []);

  useEffect(() => {
    // Skip main useEffect when on AuthCallbackPage route to prevent duplicate callback processing
    if (location.pathname === '/auth/callback') {
      console.log('[Auth] Skipping main useEffect - on AuthCallbackPage route');
      return;
    }

    // Discord OAuth callback handling
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const authSuccess = urlParams.get('auth_success');

    console.log('[Auth] useEffect triggered:', {
      hasCode: !!code,
      hasError: !!error,
      hasAuthSuccess: !!authSuccess,
      hasUser: !!user,
      alreadyAttempted: code ? authAttempts.has(code) : false
    });

    if (error) {
      console.error('[Auth] OAuth error from URL:', error);
      
      // ログインエラー・キャンセル時は確実にログイン状態をクリアしてログイン画面へ戻す
      localStorage.removeItem('token');
      setUser(null);
      setAuthAttempts(new Set());
      setLastAuthError(null);
      
      // エラーメッセージを設定
      let errorMessage = 'Discord認証でエラーが発生しました';
      
      switch (error) {
        case 'access_denied':
          errorMessage = 'Discord認証がキャンセルされました。ログインするには再度お試しください。';
          break;
        case 'no_code':
          errorMessage = '認証コードが見つかりませんでした。再度ログインをお試しください。';
          break;
        case 'server_config_error':
          errorMessage = 'サーバー設定エラーが発生しました。管理者に連絡してください。';
          break;
        case 'backend_config_error':
          errorMessage = 'バックエンド設定エラーが発生しました。管理者に連絡してください。';
          break;
        case 'token_exchange_failed':
          errorMessage = '認証トークンの取得に失敗しました。再度ログインをお試しください。';
          break;
        case 'invalid_user_data':
          errorMessage = 'ユーザー情報の取得に失敗しました。再度ログインをお試しください。';
          break;
        case 'invalid_code':
          errorMessage = '認証コードが無効です。再度ログインをお試しください。';
          break;
        case 'unauthorized':
          errorMessage = '認証に失敗しました。再度ログインをお試しください。';
          break;
        case 'network_error':
          errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください。';
          break;
        case 'auth_failed':
          errorMessage = '認証処理に失敗しました。再度ログインをお試しください。';
          break;
        default:
          errorMessage = `Discord認証エラー: ${error}`;
      }
      
      setError(errorMessage);
      setLoading(false);
      
      // Restore cast_id if it was saved
      const returnCastId = localStorage.getItem('return_cast_id');
      localStorage.removeItem('return_cast_id'); // 必ずクリア
      
      if (returnCastId) {
        console.log('[Auth] Error occurred, returning to cast_id path:', returnCastId);
        navigate(`/${returnCastId}`, { replace: true });
      } else {
        console.log('[Auth] Error occurred, returning to home (login page)');
        navigate('/', { replace: true });
      }
      return;
    }

    if (authSuccess) {
      try {
        const authData = JSON.parse(decodeURIComponent(authSuccess));
        const { token, user } = authData;
        
        if (token && user) {
          localStorage.setItem('token', token);
          setUser(user);
          setAuthAttempts(new Set()); // Clear auth attempts on success
          console.log('[Auth] Auth success processing completed for user:', user?.username);
          // Restore cast_id if it was saved
          const returnCastId = localStorage.getItem('return_cast_id');
          if (returnCastId) {
            localStorage.removeItem('return_cast_id');
            navigate(`/${returnCastId}`, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        console.error('Auth success parsing error:', error);
        setError('認証情報の処理に失敗しました');
      }
      setLoading(false);
      return;
    }

    // Only proceed with code-based auth if:
    // 1. We have a code
    // 2. User is not already logged in
    // 3. We haven't already attempted this code
    if (code && !user && !authAttempts.has(code)) {
      console.log('[Auth] Proceeding with code-based authentication');
      handleDiscordCallback(code);
    } else if (code && authAttempts.has(code)) {
      console.warn('[Auth] Skipping duplicate authentication attempt');
      setLoading(false);
    }
  }, [location, user, handleDiscordCallback, navigate, authAttempts]);

  // グローバル関数として設定（AuthCallbackPageで使用）
  useEffect(() => {
    window.handleDiscordCallback = handleDiscordCallback;
    return () => {
      delete window.handleDiscordCallback;
    };
  }, [handleDiscordCallback]);

  const clearAuthState = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setAuthAttempts(new Set());
    setLastAuthError(null);
    setError('');
  }, []);

  const handleLogin = useCallback(() => {
    // Clear any previous auth state before starting new login
    clearAuthState();
    
    // Save current cast_id (if any) to localStorage before OAuth redirect
    const currentPath = location.pathname;
    // Match paths like /cast_id or /cast_id/ (with optional trailing slash)
    const castIdMatch = currentPath.match(/^\/([^/]+)\/?$/);
    if (castIdMatch && castIdMatch[1] !== 'auth' && castIdMatch[1] !== 'success' && castIdMatch[1] !== 'cancel') {
      localStorage.setItem('return_cast_id', castIdMatch[1]);
      console.log('[Auth] Saving cast_id for return:', castIdMatch[1]);
    } else {
      localStorage.removeItem('return_cast_id');
    }
    
    const clientId = config.discord.clientId || 'your_client_id';
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
    const scope = encodeURIComponent('identify guilds.join');
    
    console.log('[Auth] Starting Discord OAuth flow:', {
      clientId: clientId.substring(0, 10) + '...',
      redirectUri,
      scope
    });
    
    window.location.href = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}`;
  }, [clearAuthState, location]);

  const handleLogout = () => {
    console.log('[Auth] Logging out user:', user?.username);
    clearAuthState();
  };

  if (loading) {
    return (
      <div className="app">
        <Header />
        <main className="main">
          <div className="container">
            <div className="loading">読み込み中...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <Header user={user} onLogout={handleLogout} />
      <main className="main">
        <div className="container">
          {error && (
            <div className="error">
              {error}
              {lastAuthError && process.env.NODE_ENV === 'development' && (
                <AuthErrorDetails error={lastAuthError} onClear={() => setLastAuthError(null)} />
              )}
            </div>
          )}
          <Routes>
            <Route 
              path="/" 
              element={
                user ? (
                  <Dashboard user={user} castId={null} />
                ) : (
                  <LoginPage onLogin={handleLogin} />
                )
              } 
            />
            <Route 
              path="/:cast_id" 
              element={
                user ? (
                  <DashboardWithCastId user={user} />
                ) : (
                  <LoginPage onLogin={handleLogin} />
                )
              } 
            />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/cancel" element={<CancelPage />} />
            <Route 
              path="/connect" 
              element={
                user ? (
                  <ConnectManagementPage />
                ) : (
                  <LoginPage onLogin={handleLogin} />
                )
              } 
            />
            <Route 
              path="/connect/return" 
              element={<ConnectReturnPage />} 
            />
            <Route 
              path="/connect/refresh" 
              element={<ConnectRefreshPage />} 
            />
          </Routes>
        </div>
      </main>
    </div>
  );
}

// Connect Account Management Page
function ConnectManagementPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newAccount, setNewAccount] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/connect/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data.accounts || []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch Connect accounts:', err);
      setError('Connectアカウントの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleCreateAccount = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/connect/onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewAccount(response.data);
      await fetchAccounts();
    } catch (err) {
      console.error('Failed to create Connect account:', err);
      
      // Check if it's a Connect not enabled error
      if (err.response?.data?.action) {
        setError(
          `${err.response.data.error}\n\n` +
          `${err.response.data.details}\n\n` +
          `対応方法: ${err.response.data.action}`
        );
      } else if (err.response?.data?.details) {
        setError(`Connectアカウントの作成に失敗しました: ${err.response.data.details}`);
      } else {
        setError('Connectアカウントの作成に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="connect-management">
      <h2>Stripe Connect アカウント管理</h2>
      
      <div className="connect-section">
        <h3>新しいConnectアカウントを作成</h3>
        <p>管理者がオンボーディングURLを生成し、分配先に手動で連絡します。</p>
        <button 
          onClick={handleCreateAccount} 
          disabled={loading}
          className="button button-primary"
        >
          {loading ? '作成中...' : '新しいアカウントを作成'}
        </button>
      </div>

      {newAccount && (
        <div className="connect-result">
          <h3>✅ アカウント作成完了</h3>
          <div className="account-details">
            <div className="detail-item">
              <strong>アカウントID:</strong>
              <code>{newAccount.accountId}</code>
            </div>
            <div className="detail-item">
              <strong>オンボーディングURL:</strong>
              <div className="url-display">
                <code>{newAccount.onboardingUrl}</code>
                <button 
                  onClick={() => navigator.clipboard.writeText(newAccount.onboardingUrl)}
                  className="button button-small"
                >
                  コピー
                </button>
              </div>
              <p className="note">
                このURLを分配先に送信してください。URLの有効期限は生成から5分間です。
              </p>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error" style={{ whiteSpace: 'pre-line' }}>{error}</div>}

      <div className="connect-section">
        <h3>既存のConnectアカウント一覧</h3>
        {loading && <div className="loading">読み込み中...</div>}
        {!loading && accounts.length === 0 && (
          <p>Connectアカウントがまだありません。</p>
        )}
        {!loading && accounts.length > 0 && (
          <table className="accounts-table">
            <thead>
              <tr>
                <th>アカウントID</th>
                <th>種別</th>
                <th>国</th>
                <th>通貨</th>
                <th>決済有効</th>
                <th>送金有効</th>
                <th>詳細提出済み</th>
                <th>作成日</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(account => (
                <tr key={account.id}>
                  <td><code>{account.id}</code></td>
                  <td>{account.type}</td>
                  <td>{account.country}</td>
                  <td>{account.default_currency?.toUpperCase()}</td>
                  <td>{account.charges_enabled ? '✅' : '❌'}</td>
                  <td>{account.payouts_enabled ? '✅' : '❌'}</td>
                  <td>{account.details_submitted ? '✅' : '❌'}</td>
                  <td>{new Date(account.created).toLocaleDateString('ja-JP')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="connect-section">
        <h3>使用方法</h3>
        <ol>
          <li>「新しいアカウントを作成」ボタンでConnectアカウントを作成</li>
          <li>生成されたオンボーディングURLを分配先に送信</li>
          <li>分配先がオンボーディングを完了（決済有効が✅になる）</li>
          <li>Stripe管理画面で商品のmetadataに設定:
            <ul>
              <li><code>ConnectAccountId1</code>: アカウントID（例: acct_xxxxx）</li>
              <li><code>ConnectAccountId1_ratio</code>: 分配率（例: 10 = 10%）</li>
              <li>複数設定する場合は、ConnectAccountId2, ConnectAccountId2_ratio... と続ける</li>
            </ul>
          </li>
        </ol>
      </div>
    </div>
  );
}

// Connect Return Page
function ConnectReturnPage() {
  return (
    <div className="connect-return">
      <h2>Connectオンボーディング完了</h2>
      <p>Stripe Connectアカウントのオンボーディングが完了しました。</p>
      <p>アカウントの設定が完了すると、決済と送金が有効になります。</p>
    </div>
  );
}

// Connect Refresh Page  
function ConnectRefreshPage() {
  return (
    <div className="connect-refresh">
      <h2>Connectオンボーディング</h2>
      <p>オンボーディングリンクの有効期限が切れています。</p>
      <p>管理者に新しいリンクの発行を依頼してください。</p>
    </div>
  );
}

function Header({ user, onLogout }) {
  return (
    <header className="header">
      <h1>🍺 Discord Bar</h1>
      <p>Discordによる大人のオンラインバーにようこそ</p>
      {user && (
        <div style={{ textAlign: 'right', marginTop: '1rem' }}>
          <button 
            onClick={onLogout}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            ログアウト
          </button>
        </div>
      )}
    </header>
  );
}

function LoginPage({ onLogin }) {
  return (
    <div className="discord-login">
      <h2>Discordでログイン</h2>
      <p>Discord Barへようこそ！Discordアカウントでログインして、ボイスチャネルの入室券やギフトアイテムを購入できます。</p>
      <button onClick={onLogin} className="discord-btn">
        <FaDiscord size={20} />
        Discordでログイン
      </button>
    </div>
  );
}

function DashboardWithCastId({ user }) {
  const { cast_id } = useParams();
  return <Dashboard user={user} castId={cast_id} />;
}

function Dashboard({ user, castId }) {
  const [products, setProducts] = useState([]);
  const [voiceChannels, setVoiceChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [configurationError, setConfigurationError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // トークンの有効性を確認
      if (!token) {
        setError('ログインセッションが見つかりません。');
        setLoading(false);
        return;
      }

      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 <= Date.now()) {
          // トークンが期限切れの場合
          localStorage.removeItem('token');
          setError('ログインセッションの有効期限が切れています。再度ログインしてください。');
          setLoading(false);
          // 数秒後にページをリロードしてログイン画面へ遷移
          setTimeout(() => window.location.reload(), 2000);
          return;
        }
      } catch (tokenError) {
        // トークンが不正な場合
        localStorage.removeItem('token');
        setError('ログイン情報が無効です。再度ログインしてください。');
        setLoading(false);
        setTimeout(() => window.location.reload(), 2000);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      console.log('[API] Loading products and voice channels...', { castId });

      // cast_idがある場合は専用エンドポイントを使用
      const productsEndpoint = castId 
        ? `${API_BASE_URL}/api/products/${castId}` 
        : `${API_BASE_URL}/api/products`;

      const [productsRes, channelsRes] = await Promise.all([
        axios.get(productsEndpoint, { headers }).catch(err => ({ error: err })),
        axios.get(`${API_BASE_URL}/api/voice-channels`, { headers }).catch(err => ({ error: err }))
      ]);

      // 商品設定エラーの処理
      if (productsRes.error) {
        const error = productsRes.error;
        console.error('[API ERROR] Products loading failed:', error.response?.data);
        
        // 認証エラーのハンドリング
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setError('ログインセッションが無効です。再度ログインしてください。');
          setLoading(false);
          setTimeout(() => window.location.reload(), 2000);
          return;
        }
        
        if (error.response?.status === 400 && error.response?.data?.error === 'Stripe product configuration incomplete') {
          setConfigurationError({
            type: 'products',
            details: error.response.data
          });
          setProducts([]);
        } else {
          setError('商品データの読み込みに失敗しました');
        }
      } else {
        setProducts(productsRes.data);
      }

      // ボイスチャネル設定の処理（voice_channel_idは任意フィールド）
      if (channelsRes.error) {
        const error = channelsRes.error;
        console.warn('[API WARNING] Voice channels loading failed (this may be normal):', error.response?.data);
        
        // 認証エラーは商品ロード時に処理されるので、ここではスキップ
        if (error.response?.status === 401) {
          // 商品APIで既にハンドリング済みのはず
          console.warn('Voice channels API returned 401 (already handled by products API)');
        }
        
        // voice_channel_idは任意フィールドなので、404や400エラーでも警告程度に留める
        if (error.response?.status === 400 && error.response?.data?.error === 'Voice channel configuration missing') {
          console.warn('Voice channels are not configured (this is optional)');
          setVoiceChannels([]);
        } else if (!productsRes.error) {
          // 商品設定に問題がなく、ボイスチャネルのみの問題の場合は警告レベル
          console.warn('ボイスチャネルデータの読み込みに失敗しました（オプション機能）');
          setVoiceChannels([]);
        }
      } else {
        setVoiceChannels(channelsRes.data);
      }

    } catch (error) {
      console.error('[API ERROR] Unexpected error:', error);
      setError('データの読み込みに失敗しました');
    }
    setLoading(false);
  }, [castId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <div className="loading">商品データを読み込み中...</div>;
  }

  return (
    <div>
      <UserInfo user={user} />
      {castId && (
        <div style={{ 
          background: '#e3f2fd', 
          padding: '0.75rem', 
          marginBottom: '1rem', 
          borderRadius: '4px',
          border: '1px solid #90caf9'
        }}>
          📍 現在のページ: <strong>{castId}</strong>
        </div>
      )}
      {configurationError && <ConfigurationErrorDisplay error={configurationError} />}
      {error && <div className="error">{error}</div>}
      {!configurationError && <ProductList products={products} voiceChannels={voiceChannels} castId={castId} />}
    </div>
  );
}

function ConfigurationErrorDisplay({ error }) {
  const { type, details } = error;
  
  return (
    <div className="configuration-error" style={{
      background: '#fee',
      border: '2px solid #f88',
      borderRadius: '8px',
      padding: '1.5rem',
      margin: '1rem 0',
      color: '#800'
    }}>
      <h3 style={{ margin: '0 0 1rem 0', color: '#c00' }}>🚨 システム設定エラー</h3>
      
      {type === 'products' && (
        <div>
          <p><strong>商品設定に不備があります。現在決済を受け付けできません。</strong></p>
          <p>Stripe商品のmetadata設定が不完全です。管理者に連絡してください。</p>
          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>設定エラー詳細</summary>
            <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
              <p>対象商品数: {details.totalProductsCount} / 正常設定商品: {details.validProductsCount}</p>
              {details.details && details.details.map((item, idx) => (
                <div key={idx} style={{ margin: '0.5rem 0', padding: '0.5rem', background: '#fdd', borderRadius: '4px' }}>
                  <strong>{item.productName} (ID: {item.productId})</strong>
                  <ul style={{ margin: '0.25rem 0 0 1rem' }}>
                    {item.errors.map((err, i) => (
                      <li key={i} style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontWeight: 'bold', color: '#c00' }}>{err.message}</div>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          設定必要: {err.required}
                        </div>
                        {err.example && (
                          <div style={{ fontSize: '0.8em', color: '#888', fontFamily: 'monospace' }}>
                            例: {err.example}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

function UserInfo({ user }) {
  return (
    <div className="user-info">
      <img 
        src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`}
        alt="User Avatar"
        className="user-avatar"
      />
      <div className="user-details">
        <h3>{user.username}</h3>
        <p>Discord ID: {user.id}</p>
      </div>
    </div>
  );
}

function ProductList({ products, voiceChannels, castId }) {
  // プルダウン・チャネル選択を廃止し、購入ボタンのみ
  const [purchasing, setPurchasing] = useState({});
  const [purchaseError, setPurchaseError] = useState('');

  const handlePurchase = async (product) => {
    try {
      setPurchasing(prev => ({ ...prev, [product.id]: true }));
      setPurchaseError('');
      
      const token = localStorage.getItem('token');
      
      // トークンの有効性を確認
      if (!token) {
        setPurchaseError('ログインセッションが見つかりません。再度ログインしてください。');
        setPurchasing(prev => ({ ...prev, [product.id]: false }));
        return;
      }

      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 <= Date.now()) {
          // トークンが期限切れの場合、再ログインを促す
          localStorage.removeItem('token');
          setPurchaseError('ログインセッションの有効期限が切れています。再度ログインしてください。');
          setPurchasing(prev => ({ ...prev, [product.id]: false }));
          // 数秒後にページをリロードしてログイン画面へ遷移
          setTimeout(() => window.location.reload(), 2000);
          return;
        }
      } catch (tokenError) {
        // トークンが不正な場合
        localStorage.removeItem('token');
        setPurchaseError('ログイン情報が無効です。再度ログインしてください。');
        setPurchasing(prev => ({ ...prev, [product.id]: false }));
        setTimeout(() => window.location.reload(), 2000);
        return;
      }

      // metadataからチャネルID等を取得
      const targetVcId = product.metadata?.voice_channel_id || '';

      console.log('[API] Creating checkout session...', {
        productId: product.id,
        productName: product.name,
        priceId: product.stripe_price_id,
        targetVcId: targetVcId,
        castId: castId
      });

      const response = await axios.post(`${API_BASE_URL}/api/create-checkout-session`, {
        priceId: product.stripe_price_id,
        targetVcId,
        castId: castId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('[API] Checkout session created successfully:', response.data);

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('[API ERROR] Purchase error:', error.response?.data || error.message);
      
      const errorData = error.response?.data;
      
      // 認証エラーの特別処理
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setPurchaseError('ログインセッションが無効です。再度ログインしてください。');
        setTimeout(() => window.location.reload(), 2000);
      } else if (errorData && errorData.error) {
        if (errorData.error === 'Product configuration invalid') {
          const errorDetails = errorData.validationErrors?.map(err => 
            `${err.message}\n設定必要: ${err.required}${err.example ? `\n例: ${err.example}` : ''}`
          ).join('\n\n') || 'metadata設定不備';
          
          setPurchaseError(`❌ 商品設定エラー: ${errorData.productName}\n\n設定不備により購入できません。管理者に連絡してください。\n\n詳細:\n${errorDetails}`);
        } else if (errorData.error === 'Product out of stock') {
          setPurchaseError(`❌ 在庫切れ: ${errorData.productName}\n\n残り在庫: ${errorData.stockStatus?.remaining || 0}個`);
        } else {
          setPurchaseError(`購入処理でエラーが発生しました: ${errorData.message || errorData.error}`);
        }
      } else {
        setPurchaseError('購入処理でエラーが発生しました。しばらく待ってから再試行してください。');
      }
    }
    setPurchasing(prev => ({ ...prev, [product.id]: false }));
  };

  return (
    <div className="products">
      <h2>🛍️ 商品一覧</h2>
      {purchaseError && (
        <div className="error" style={{ 
          whiteSpace: 'pre-line', 
          marginBottom: '1rem',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          padding: '1rem'
        }}>
          {purchaseError}
        </div>
      )}
      <div className="product-grid">
        {products.map(product => (
          <ProductCard
            key={product.id}
            product={product}
            onPurchase={() => handlePurchase(product)}
            purchasing={purchasing[product.id]}
          />
        ))}
      </div>
    </div>
  );
}

function ProductCard({ product, onPurchase, purchasing }) {
  // metadataからロール・チャネル情報を抽出（仕様準拠キー）
  const { voice_channel_name, voice_channel_id, role_id, notify_channel_id } = product.metadata || {};
  const isConfigValid = product.configurationValid !== false;

  // Fetch Discord role and channel names dynamically
  const { discordInfo, loading: discordInfoLoading } = useDiscordInfo(role_id, notify_channel_id);
  
  return (
    <div className="product-card" style={{
      opacity: isConfigValid ? 1 : 0.6,
      border: isConfigValid ? '1px solid #ddd' : '2px solid #f88'
    }}>
      <h3>{product.name}</h3>
      {!isConfigValid && (
        <div style={{ 
          background: '#fee', 
          color: '#c00', 
          padding: '0.5rem', 
          borderRadius: '4px', 
          marginBottom: '0.5rem',
          fontSize: '0.9em'
        }}>
          ⚠️ 設定不備により購入不可
        </div>
      )}
      {product.image && (
        <img src={product.image} alt={product.name} style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
      )}
      <p>{product.description}</p>
      <p><strong>価格: {product.currency === 'jpy' ? `¥${product.price}` : `${product.price} ${product.currency?.toUpperCase()}`}</strong></p>

      {/* metadata情報を表示 */}
      <div className="product-meta">
        {voice_channel_name && (
          <div><strong>対象ボイスチャネル:</strong> {voice_channel_name} <span style={{ color: '#888', fontSize: '0.9em' }}>({voice_channel_id})</span></div>
        )}
        {role_id && (
          <div>
            <strong>付与ロール:</strong> 
            <span style={{ color: '#888', fontSize: '0.9em' }}>
              {discordInfoLoading ? '読み込み中...' : (discordInfo.role?.name || role_id)}
            </span>
          </div>
        )}
        {notify_channel_id && (
          <div>
            <strong>通知チャンネル:</strong> 
            <span style={{ color: '#888', fontSize: '0.9em' }}>
              {discordInfoLoading ? '読み込み中...' : (discordInfo.channel?.name || notify_channel_id)}
            </span>
          </div>
        )}
      </div>

      <button 
        className="purchase-btn"
        onClick={onPurchase}
        disabled={purchasing || !isConfigValid || (product?.type === 'role_grant' && product.stock.remaining <= 0)}
        style={{
          opacity: (purchasing || !isConfigValid || (product?.type === 'role_grant' && product.stock.remaining <= 0)) ? 0.5 : 1,
          cursor: (purchasing || !isConfigValid || (product?.type === 'role_grant' && product.stock.remaining <= 0)) ? 'not-allowed' : 'pointer'
        }}
      >
        {purchasing ? '処理中...' : 
         !isConfigValid ? '設定不備のため購入不可' :
         (product?.type === 'role_grant' && product.stock.remaining <= 0) ? '在庫切れ' :
         '購入する'}
      </button>
    </div>
  );
}

function SuccessPage() {
  const location = useLocation();
  const [processingStatus, setProcessingStatus] = useState('processing');
  const [statusMessage, setStatusMessage] = useState('Discord側の処理を確認しています...');
  const [error, setError] = useState(null);
  const [returnPath, setReturnPath] = useState('/');

  useEffect(() => {
    // Get session ID and cast_id from URL query parameters
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const castId = params.get('cast_id');
    
    // Set return path based on cast_id
    if (castId) {
      setReturnPath(`/${castId}`);
      console.log('[SuccessPage] Will return to cast_id page:', castId);
    }
    
    if (!sessionId) {
      setError('セッションIDが見つかりません。');
      setProcessingStatus('error');
      return;
    }
    
    // Start polling for status
    const pollStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('認証が必要です。ログインしてください。');
          setProcessingStatus('error');
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const status = response.data;
        
        switch (status.processing_status) {
          case 'payment_pending':
            setStatusMessage('決済処理中です...');
            setProcessingStatus('processing');
            break;
            
          case 'processing':
            setStatusMessage('Discord側の処理中です。しばらくお待ちください...');
            setProcessingStatus('processing');
            break;
            
          case 'completed':
            setStatusMessage('購入が正常に完了しました！Discordでの処理も完了しています。');
            setProcessingStatus('success');
            break;
            
          case 'error':
            setError(`❌ 決済は完了しましたが、Discord側の処理でエラーが発生しました。

【エラー内容】
${status.error_message}

【管理者向け詳細情報】
${status.error_details ? JSON.parse(status.error_details).message : '詳細情報なし'}

このエラー内容を管理者にお伝えください。決済は完了しているため、手作業で復旧いたします。`);
            setProcessingStatus('error');
            break;
            
          case 'timeout':
            setError(`⚠️ 処理に時間がかかっています。

決済は完了していますが、Discord側の処理が予想以上に時間がかかっています。
しばらく待ってもDiscordでの変更が反映されない場合は、以下の情報と共に管理者にご連絡ください：

【セッションID】
${sessionId}

【支払い情報】
金額: ${status.amount_total ? `¥${status.amount_total}` : '不明'}
決済日時: ${status.created_at}

管理者が手作業で復旧いたします。`);
            setProcessingStatus('timeout');
            break;
            
          default:
            setError('不明な処理状態です。管理者にお問い合わせください。');
            setProcessingStatus('error');
        }
      } catch (err) {
        console.error('Status check error:', err);
        if (err.response?.status === 404) {
          setError('セッションが見つかりません。URLが正しいかご確認ください。');
        } else if (err.response?.status === 401) {
          setError('認証が必要です。ログインしてください。');
        } else {
          setError('処理状況の確認中にエラーが発生しました。ページを再読み込みしてお試しください。');
        }
        setProcessingStatus('error');
      }
    };

    // Initial status check
    pollStatus();
    
    // Poll every 3 seconds if still processing
    const interval = setInterval(() => {
      if (processingStatus === 'processing') {
        pollStatus();
      } else {
        clearInterval(interval);
      }
    }, 3000);

    // Clean up interval on unmount or when processing completes
    return () => clearInterval(interval);
  }, [location.search, processingStatus]);

  if (processingStatus === 'processing') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="processing">
          <h2>🔄 処理中...</h2>
          <p>{statusMessage}</p>
          <div style={{ marginTop: '1rem' }}>
            <div className="loading-spinner" style={{ 
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #7289da',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
            このページを閉じずにお待ちください。処理が完了すると自動的に更新されます。
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (processingStatus === 'success') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="success">
          <h2>🎉 購入が完了しました！</h2>
          <p>{statusMessage}</p>
          <p>ありがとうございます。</p>
          <button 
            onClick={() => window.location.href = returnPath}
            className="discord-btn"
            style={{ marginTop: '1rem' }}
          >
            ホームに戻る
          </button>
        </div>
      </div>
    );
  }

  // Error or timeout state
  return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <div className="error">
        <h2>⚠️ 処理状況</h2>
        <div style={{ 
          textAlign: 'left', 
          backgroundColor: '#f8f8f8', 
          padding: '1rem', 
          borderRadius: '5px', 
          margin: '1rem 0',
          whiteSpace: 'pre-line',
          fontSize: '0.9em'
        }}>
          {error}
        </div>
        <button 
          onClick={() => window.location.href = returnPath}
          className="discord-btn"
          style={{ marginTop: '1rem' }}
        >
          ホームに戻る
        </button>
      </div>
    </div>
  );
}

function CancelPage() {
  const location = useLocation();
  
  // Get cast_id from URL query parameters
  const params = new URLSearchParams(location.search);
  const castId = params.get('cast_id');
  const returnPath = castId ? `/${castId}` : '/';
  
  return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <div className="error">
        <h2>❌ 購入がキャンセルされました</h2>
        <p>購入処理がキャンセルまたは失敗しました。</p>
        <button 
          onClick={() => window.location.href = returnPath}
          className="discord-btn"
          style={{ marginTop: '1rem' }}
        >
          ホームに戻る
        </button>
      </div>
    </div>
  );
}

function AuthCallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const error = params.get('error');

    console.log('[AuthCallback] Processing callback:', { hasCode: !!code, hasError: !!error });

    if (error) {
      console.error('[AuthCallback] Discord OAuth error:', error);
      // エラー時はログイン状態をクリアしてトップページへ
      localStorage.removeItem('token');
      localStorage.removeItem('return_cast_id');
      navigate(`/?error=${error}`, { replace: true });
      return;
    }

    if (code) {
      // URLからコードを取得してコールバック処理を実行
      console.log('[AuthCallback] Calling global handleDiscordCallback with code:', code.substring(0, 10) + '...');
      if (window.handleDiscordCallback) {
        window.handleDiscordCallback(code);
      } else {
        console.error('[AuthCallback] handleDiscordCallback not available');
        navigate('/?error=callback_handler_missing', { replace: true });
      }
    } else {
      console.warn('[AuthCallback] No code found in callback');
      // コードもエラーもない場合はログイン画面へ
      localStorage.removeItem('token');
      localStorage.removeItem('return_cast_id');
      navigate('/?error=no_auth_code', { replace: true });
    }
  }, [location, navigate]);

  return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <div>
        <h2>認証処理中...</h2>
        <p>Discordアカウントの認証を処理しています。しばらくお待ちください。</p>
      </div>
    </div>
  );
}

function AuthErrorDetails({ error, onClear }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div style={{ 
      marginTop: '1rem', 
      padding: '1rem', 
      backgroundColor: '#f8f8f8', 
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '0.9em'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>認証エラー詳細 (開発モード)</strong>
        <div>
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ marginRight: '0.5rem', fontSize: '0.8em' }}
          >
            {isExpanded ? '非表示' : '詳細表示'}
          </button>
          <button onClick={onClear} style={{ fontSize: '0.8em' }}>クリア</button>
        </div>
      </div>
      
      {isExpanded && (
        <div style={{ marginTop: '0.5rem', fontFamily: 'monospace', fontSize: '0.8em' }}>
          <div><strong>タイムスタンプ:</strong> {error.timestamp}</div>
          <div><strong>認証コード:</strong> {error.code}</div>
          <div><strong>エラーメッセージ:</strong> {error.message}</div>
          {error.status && <div><strong>HTTPステータス:</strong> {error.status}</div>}
          {error.responseData && (
            <div>
              <strong>レスポンスデータ:</strong>
              <pre style={{ 
                marginTop: '0.5rem', 
                padding: '0.5rem', 
                backgroundColor: '#eee',
                borderRadius: '2px',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                {JSON.stringify(error.responseData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
